import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "./context/AppContext";
import { Layout } from "./components/layout/Layout";

// Pages
import { Dashboard } from "./components/pages/Dashboard";
import { TicketList } from "./components/pages/TicketList";
import { TicketDetail } from "./components/pages/TicketDetail";
import { NewTicket } from "./components/pages/NewTicket";
import { Approvals } from "./components/pages/Approvals";
import { AdminTicketQueue } from "./components/pages/AdminTicketQueue";
import { AdminSoftware } from "./components/pages/AdminSoftware";
import { AdminResources } from "./components/pages/AdminResources";
import { AdminEquipment } from "./components/pages/AdminEquipment";
import { AdminDepartments } from "./components/pages/AdminDepartments";
import { AdminUsers } from "./components/pages/AdminUsers";
import { AdminWorkstationTemplates } from "./components/pages/AdminWorkstationTemplates";
import { AdminWorkflows } from "./components/pages/AdminWorkflows";
import { AdminLdap } from "./components/pages/AdminLdap";
import { AdminIntegrations } from "./components/pages/AdminIntegrations";
import { AdminTicketCategories } from "./components/pages/AdminTicketCategories";
import { LoginPage } from "./components/pages/LoginPage";
import { LocalLoginPage } from "./components/pages/LocalLoginPage";
import AdminKnowledgeBase from "./components/pages/AdminKnowledgeBase";
import PublicKnowledgeBase from "./components/pages/PublicKnowledgeBase";
import AdminAutomation from "./components/pages/AdminAutomation";
import AdminMacros from "./components/pages/AdminMacros";
import AdminSettings from "./components/pages/AdminSettings";
import AdminChecklistTemplates from "./components/pages/AdminChecklistTemplates";
import { AdminEntra } from "./components/pages/AdminEntra";
import { AdminPositions } from "./components/pages/AdminPositions";
import { TeamManagement } from "./components/pages/TeamManagement";
import AdminTeamRequests from "./components/pages/AdminTeamRequests";
import { TicketSearch } from "./components/pages/TicketSearch";
import UserProfile from "./components/pages/UserProfile";
import Inventory from "./components/pages/Inventory";
import InventoryDetail from "./components/pages/InventoryDetail";
import InventoryEmployeeSheet from "./components/pages/InventoryEmployeeSheet";
import Printers from "./components/pages/Printers";
import PrinterDetail from "./components/pages/PrinterDetail";
import ScanRedirect from "./components/pages/ScanRedirect";
import AdminAccess from "./components/pages/AdminAccess";

function ProtectedRoute({ children }) {
    const { authUser, loading } = useApp();

    if (authUser === null || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex items-center gap-3 text-gray-500">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    <span className="text-sm">Chargement...</span>
                </div>
            </div>
        );
    }

    if (authUser === false) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

function PublicRoute({ children }) {
    const { authUser } = useApp();

    if (authUser === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex items-center gap-3 text-gray-500">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    <span className="text-sm">Chargement...</span>
                </div>
            </div>
        );
    }

    if (authUser && authUser !== false) {
        return <Navigate to="/" replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/login/local" element={<PublicRoute><LocalLoginPage /></PublicRoute>} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                {/* User Routes */}
                <Route path="/" element={<Dashboard />} />
                <Route path="/nouveau" element={<NewTicket />} />
                <Route path="/tickets" element={<TicketList />} />
                <Route path="/tickets/:id" element={<TicketDetail />} />
                <Route path="/approbations" element={<Approvals />} />
                <Route path="/team" element={<TeamManagement />} />
                <Route path="/search" element={<TicketSearch />} />
                <Route path="/users/:id/profile" element={<UserProfile />} />
                <Route path="/base-de-connaissances" element={<PublicKnowledgeBase />} />

                {/* Admin Routes */}
                <Route path="/admin/tickets" element={<AdminTicketQueue />} />
                <Route path="/admin/utilisateurs" element={<AdminUsers />} />
                <Route path="/admin/departements" element={<AdminDepartments />} />
                <Route path="/admin/positions" element={<AdminPositions />} />
                <Route path="/admin/logiciels" element={<AdminSoftware />} />
                <Route path="/admin/ressources" element={<AdminResources />} />
                <Route path="/admin/materiel" element={<AdminEquipment />} />
                <Route path="/admin/postes" element={<AdminWorkstationTemplates />} />
                <Route path="/admin/workflows" element={<AdminWorkflows />} />
                <Route path="/admin/ldap" element={<AdminLdap />} />
                <Route path="/admin/integrations" element={<AdminIntegrations />} />
                <Route path="/admin/categories-tickets" element={<AdminTicketCategories />} />
                <Route path="/admin/base-de-connaissances" element={<AdminKnowledgeBase />} />
                <Route path="/admin/automatisation" element={<AdminAutomation />} />
                <Route path="/admin/macros" element={<AdminMacros />} />
                <Route path="/admin/parametres" element={<AdminSettings />} />
                <Route path="/admin/checklist-templates" element={<AdminChecklistTemplates />} />
                <Route path="/admin/entra" element={<AdminEntra />} />
                <Route path="/admin/demandes-equipe" element={<AdminTeamRequests />} />
                <Route path="/admin/acces" element={<AdminAccess />} />
                <Route path="/admin/inventory" element={<Inventory />} />
                <Route path="/admin/inventory/printers" element={<Printers />} />
                <Route path="/admin/inventory/printers/:id" element={<PrinterDetail />} />
                <Route path="/admin/inventory/employee/:id" element={<InventoryEmployeeSheet />} />
                <Route path="/admin/inventory/:id" element={<InventoryDetail />} />
                <Route path="/scan/:tag" element={<ScanRedirect />} />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AppProvider>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
        </AppProvider>
    );
}

export default App;
